import { useEffect, useRef, useState } from 'react';

import i18n from '../../locales/i18n';
import { ActionTargetReference, DeploymentCompatibilityPreview } from '../../types/actionTypes';
import { previewDeploymentCompatibility } from '../../services/rtuService';

export type DeploymentPreviewState = 'idle' | 'loading' | 'ready' | 'error';

export const useDeploymentCompatibilityPreview = (file: File | null, targets: ActionTargetReference[]) => {
  const [preview, setPreview] = useState<DeploymentCompatibilityPreview | null>(null);
  const [state, setState] = useState<DeploymentPreviewState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [previewRequestKey, setPreviewRequestKey] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const generationRef = useRef(0);
  const targetsRef = useRef(targets);
  targetsRef.current = targets;
  // File-list identity is Well ID + serial. This preview remains keyed on
  // serial until configuration/deployment targeting scope is defined.
  const targetKey = JSON.stringify(
    targets.map((target) => target.serialNo).sort((left, right) => left.localeCompare(right)),
  );
  const requestKey = file
    ? `${file.name}:${file.size}:${file.lastModified}:${targetKey}`
    : `no-file:${targetKey}`;

  const retry = () => {
    // A state nonce deliberately restarts the effect even when the selected
    // file and RTU set have not changed.
    setRetryNonce((current) => current + 1);
  };

  useEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    setPreview(null);
    setPreviewRequestKey(null);
    setError(null);
    if (!file || targetsRef.current.length === 0) {
      setState('idle');
      return undefined;
    }

    const controller = new AbortController();
    setState('loading');
    void previewDeploymentCompatibility(targetsRef.current, file.name, controller.signal)
      .then((response) => {
        if (generation === generationRef.current) {
          setPreview(response);
          setPreviewRequestKey(requestKey);
          setState('ready');
        }
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted || generation !== generationRef.current) return;
        const message = (requestError as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
          || (requestError as Error)?.message
          || i18n.t('actionsModal.compatibility.failed');
        setError(message);
        setState('error');
      });
    return () => controller.abort();
  }, [file, requestKey, retryNonce, targetKey]); // requestKey binds a preview to the exact file and target set.

  const current = state === 'ready' && previewRequestKey === requestKey;
  return { current, error, preview: current ? preview : null, retry, state: current || state !== 'ready' ? state : 'loading' };
};
